import React, { FC, useEffect, useRef, useState } from 'react';
import attachImage from 'assets/svg/attach file.svg';
import sendImage from 'assets/svg/send_white.svg';
import Styles from 'components/chatContent/chatFooter/ChatFooter.module.scss';
import { randomString, getCurrentDate } from 'helpers/utils';
import { sendFCMMessage, sendThreadMessage, updateNewRoomStatus, uploadImage } from 'store/services/firebase-broadcast';
import { Attachment } from 'modules/types/types';
import { attachment } from 'helpers/data/general';
import moment from 'moment';
import { invitePeople, invitePeopleRequest } from 'store/actions/general.actions';
import { connect } from 'react-redux';

interface ChatFooterProps {
  typing: Function;
  selected: any;
  chats?: any;
  isThread?: boolean;
  parentMessage?: object;
  images?: Function;
  clearImages?: Function;
  activateRoom?: any;
  invitePeople: Function;
  attachedImages?: any;
  onMessageSent?: () => void;
}

const ChatFooter: FC<ChatFooterProps> = (props: ChatFooterProps) => {
  const { typing, selected, chats, isThread, parentMessage, images, clearImages, activateRoom, invitePeople, attachedImages, onMessageSent } = props;
  const [file, setFile] = useState<Attachment>(attachment);
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<any>([]);
  const [send, setSend] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(40);
  const inputRef: any = useRef(null);
  const textInputRef: any = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [sender] = useState({
    id: user?.pk,
    name: user.name,
    image: user.profile,
    isHost: user?.pk === selected.host?.pk,
  });

  const handleMessage = async () => {
    if (isThread) {
      await sendThread();
    } else {
      await sendMessage();
      let updateRoom = {
        room:activateRoom,
        activeRoom: moment().format()
      };
      updateNewRoomStatus(updateRoom)
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (message.trim().length > 0 || attachments.length > 0) {
        handleMessage();
      }
    } else if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      const cursorPosition = e.currentTarget.selectionStart;
      if (cursorPosition !== null) {
        const textBeforeCursor = message.substring(0, cursorPosition);
        const textAfterCursor = message.substring(cursorPosition);
        const newMessage = textBeforeCursor + '\n' + textAfterCursor;
        setMessage(newMessage);
        
        // Set cursor position after the new line using a more reliable approach
        setTimeout(() => {
          const inputElement = textInputRef.current;
          if (inputElement && inputElement.setSelectionRange) {
            try {
              inputElement.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
              inputElement.focus();
            } catch (error) {
              console.warn('Failed to set cursor position:', error);
            }
          }
        }, 10);
      }
    }
  };

  const handleAttachment = (e: any) => {
    if (e.target.files.length > 0) {
      let type = 'image';
      let fileType = e.target.files[0].type;
      fileType = fileType.split('/');
      if (fileType[0] === 'video') type = 'video';
      const obj = {
        id: `${selected?.pk}-${randomString()}`,
        name: e.target.files[0].name,
        path: `${URL.createObjectURL(e.target.files[0])}`,
        file: e.target.files[0],
        type: type,
      };
      setFile(obj);
      setAttachments([...attachments, obj.path]);
      images && images(obj);
    }
  };

  const sendMessage = async () => {
    const msg = {
      roomId: selected?.pk,
      id: `${selected?.pk}-${randomString()}`,
      sender: sender,
      receiver: {},
      message: message,
      time: getCurrentDate(),
      is_delete: false,
      attachments: [file],
      threads: [],
    };
    
    try {
      // If there's a file attachment, upload it first, then send the message
      if (file.id !== '') {
        await uploadImage(msg, file);
        await sendFCMMessage(msg);
      } else {
        // No attachment, send message immediately
        await sendFCMMessage(msg);
      }
      
      // Only clear state if message was sent successfully
      setMessage('');
      setFile(attachment);
      setAttachments([]); // Clear the attachments array
      
      // Clear the images in the parent component to clear attachment list
      if (clearImages && typeof clearImages === 'function') {
        clearImages();
      }

      // Notify parent to scroll to bottom
      onMessageSent && onMessageSent();
    } catch (error: any) {
      // Skip handling if user data is invalid (toast already shown by middleware)
      if (error.code === 'INVALID_USER_DATA') {
        return;
      }
      console.error('Failed to send message:', error);
    }
  };

  const sendThread = async () => {
    const { id }: any = parentMessage;
    const msg = {
      parentId: id,
      roomId: selected?.pk,
      id: `${selected?.pk}-${randomString()}`,
      sender: sender,
      receiver: {},
      message: message,
      time: getCurrentDate(),
      is_delete: false,
      attachments: [file], // Use the selected file instead of empty array
      threads: [],
    };
    
    try {
      // If there's a file attachment, upload it first, then send the thread message
      if (file.id !== '') {
        await uploadImage(msg, file);
        await sendThreadMessage(msg);
      } else {
        // No attachment, send thread message immediately
        await sendThreadMessage(msg);
      }
      
      // Only clear state if message was sent successfully
      setMessage('');
      setFile(attachment);
      setAttachments([]); // Clear the attachments array
      
      // Clear the images in the parent component to clear attachment list
      if (clearImages && typeof clearImages === 'function') {
        clearImages();
      }

      // Notify parent to scroll to bottom
      onMessageSent && onMessageSent();
    } catch (error: any) {
      // Skip handling if user data is invalid (toast already shown by middleware)
      if (error.code === 'INVALID_USER_DATA') {
        return;
      }
      console.error('Failed to send thread message:', error);
    }
  };

  useEffect(() => {
    if (message.length > 0) {
      setSend(true);
      typing(true);
    } else {
      setSend(false);
      typing(false);
    }
  }, [message]);

  useEffect(() => {
    // Add null check for attachedImages before calling filter
    if (attachedImages && Array.isArray(attachedImages)) {
      const noFile = attachedImages.filter((i:any) => i.file === inputRef?.current?.files[0]);
      if (noFile.length === 0) {
        inputRef.current.value = '';
      }
    }
  }, [attachedImages])

  // Auto-resize textarea based on content with better height management
  useEffect(() => {
    if (textInputRef.current) {
      const textarea = textInputRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = 'auto';
      
      // Get computed styles to calculate accurate line height
      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 24;
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      
      const maxLines = 1; // Max 1 line
      // Calculate max height for 1 line: line height + top padding + bottom padding
      const maxHeight = (lineHeight * maxLines) + paddingTop + paddingBottom;
      
      // Calculate new height with constraints (max 1 line, but at least minHeight)
      const minHeight = 40;
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      
      // Update state for height tracking
      setTextareaHeight(newHeight);
      
      // Apply height to textarea
      textarea.style.height = `${newHeight}px`;
      
      // Always hide scrollbar - never show it
      textarea.style.overflowY = 'hidden';
      textarea.style.overflowX = 'hidden';
      
      // Ensure the parent container adjusts smoothly
      if (textarea.parentElement) {
        textarea.parentElement.style.transition = 'height 0.2s ease';
      }
    }
  }, [message]);
  return (
    <div className={`${Styles.ChatFooter}`}>
      <div className={`${Styles.messageBox} position-relative`} style={{ 
        minHeight: '40px',
        height: `${textareaHeight}px`,
        transition: 'height 0.2s ease'
      }}>
        <div className={Styles.attachButtonWrapper}>
          <div className={`position-relative ${Styles.attachButton}`}>
            <input ref={inputRef} type="file" onChange={handleAttachment} />
            <img src={attachImage} alt="attach file" />
          </div>
        </div>
        
        {/* Text input area */}
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <textarea
            ref={textInputRef}
            value={message}
            placeholder="Type your message…"
            className={`w-100 ${Styles.typingArea}`}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            style={{ 
              resize: 'none', 
              overflow: 'hidden',
              overflowY: 'hidden',
              overflowX: 'hidden',
              minHeight: '40px',
              lineHeight: '24px',
              padding: '8px 12px',
              transition: 'height 0.2s ease',
              boxSizing: 'border-box',
              wordWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              position: 'relative',
              zIndex: 1,
              fontSize: '12px' // Set to 12px
            }}
          />
        </div>
      </div>
      {send || attachments.length > 0 ? (
        <div onClick={handleMessage} onKeyPress={handleMessage} role="button" tabIndex={0}>
          <img src={sendImage} className={Styles.sendIcon} alt="send icon" />
        </div>
      ) : (
        <div>
          <img src={sendImage} className={Styles.sendIcon} alt="send icon" />
        </div>
      )}
    </div>
  );
};
const mapDispatchToProps = { invitePeople: invitePeopleRequest };
export default connect(null, mapDispatchToProps)(ChatFooter);
